import { Injectable } from '@angular/core';
import { Map, OrderedMap, Set } from 'immutable';

import {
	BeElement,
	IMarketplaceGroup,
	IMarketplaceUser,
	PermissionResponse,
} from '../../requesters';
import dayjs from '../configs/dayjs';
import { DcCoreElementType } from '../dc-core-types';
import { MarketplaceMemberEntity } from '../domain/marketplace-member.entity';
import { ElementPermission } from '../domain/permission-types';

@Injectable({
	providedIn: 'root',
})
export class AppAdapter {
	private readonly elementMap: Record<BeElement, DcCoreElementType> = {
		datablock: DcCoreElementType.DATABLOCK,
		dashboard: DcCoreElementType.DASHBOARD,
		users: DcCoreElementType.USERS,
		exposition: DcCoreElementType.EXPOSITION,
		entity: DcCoreElementType.ENTITY,
		connector: DcCoreElementType.CONNECTOR,
		map: DcCoreElementType.MAP,
		media: DcCoreElementType.MEDIA,
		timeline: DcCoreElementType.TIMELINE,
		chart: DcCoreElementType.CHART,
		grid: DcCoreElementType.GRID,
		['data_source']: DcCoreElementType.DATASOURCE,
		pictogramme: DcCoreElementType.PICTOGRAMME,
		tag: DcCoreElementType.TAG,
		column: DcCoreElementType.COLUMN,
		['export_template']: DcCoreElementType.EXPORT_TEMPLATE,
		datalink: DcCoreElementType.DATALINK,
	};

	public generatePermissions(
		response: PermissionResponse
	): Map<DcCoreElementType, Set<ElementPermission>> {
		return Object.entries(response).reduce((acc, [beElement, actions]) => {
			if (!actions) {
				return acc;
			}

			const dcElement = this.elementMap[beElement as BeElement];
			if (!dcElement) {
				return acc;
			}

			const filteredActions: Array<ElementPermission> = actions.filter(
				(action) =>
					action !== ElementPermission.List &&
					action !== ElementPermission.Create
			);

			return acc.set(dcElement, Set(filteredActions));
		}, Map<DcCoreElementType, Set<ElementPermission>>());
	}

	public generateUserMembers(
		response: ReadonlyArray<IMarketplaceUser>
	): Map<string, MarketplaceMemberEntity> {
		const items = response.reduce((acc, user, index) => {
			const entity = MarketplaceMemberEntity.build({
				id: user.id,
				login: user.login,
				firstName: user.firstname,
				lastName: user.lastname,
				name: `${user.firstname} ${user.lastname}`,
				mail: user.mail,
				isGroup: false,
				creationDate: user.created_at ? dayjs(user.created_at) : null,
				updateDate: user.updated_at ? dayjs(user.updated_at) : null,
			});
			acc.push([`${entity.id}`, entity]);
			return acc;
		}, new Array<[string, MarketplaceMemberEntity]>());

		return OrderedMap(items);
	}

	public generateGroupMembers(
		response: ReadonlyArray<IMarketplaceGroup>
	): Map<string, MarketplaceMemberEntity> {
		const items = response.reduce((acc, group, index) => {
			const entity = MarketplaceMemberEntity.build({
				id: group.id,
				name: group.name,
				isGroup: true,
				description: group.description,
				creationDate: group.created_at ? dayjs(group.created_at) : null,
				updateDate: group.updated_at ? dayjs(group.updated_at) : null,
			});
			acc.push([`${entity.id}`, entity]);
			return acc;
		}, new Array<[string, MarketplaceMemberEntity]>());

		return OrderedMap(items);
	}
}
